import { PaymentType } from '@ace-clublabs/payment-types';
import React, { useCallback, useEffect, useState } from 'react';
import CardForm from '../components/CardForm';
import ECheckForm from '../components/ECheckForm';
import ECheckFormAdyen from '../components/ECheckFormAdyen';
import { UtagHelper } from '../utils/utagHelper';
import { useGlobalState } from '../shared/state';
import { Pages } from '../shared/pages';
import { useNavigate } from 'react-router-dom';
import CardFormAyden from '../components/CardFormAdyen';
import { sources } from '../api/sources';
import Processing from '../components/Processing';
import LogCannon from '@ace-clublabs/log-cannon';
import { getPaymentPBLTaggingFlow, getPaymentPBLTaggingInsurancePolicies } from '../utils/methods';
import { validatePBLExpiration } from '../utils/pblExpirationHelper';

const PaymentPage: React.FC = () => {
  const [manifest] = useGlobalState('manifest');
  const [manifestId] = useGlobalState('manifestId');
  const [paymentType] = useGlobalState('paymentType');
  const [redirectMap] = useGlobalState('redirectMap');
  const [scheduledDate] = useGlobalState('scheduledDate');
  const [isScheduled] = useGlobalState('isScheduled');
  const [sessionId] = useGlobalState('sessionId');
  const [customerId] = useGlobalState('customerId');
  const [payByLinkData] = useGlobalState('payByLinkData');
  const [, setError] = useGlobalState('error');
  const [tagged, setTagged] = useState(false);
  const [showCardAdyen, setShowCardAdyen] = useState(false);
  const [showFDCardForm, setShowFDCardForm] = useState(false);
  const [isProcessing, setProcessing] = useState({
    status: true,
    text: 'Your checkout is loading',
    cindy: true,
    displayCheckmark: false,
    willRedirect: false,
    callback: () => {},
  });
  const navigate = useNavigate();

  /** Stable handler so Processing’s delayed onFinished always runs the latest callback (functional update). */
  const onProcessingFinished = useCallback(() => {
    setProcessing((prev) => {
      prev.callback?.();
      return {
        status: prev.willRedirect,
        text: '',
        cindy: false,
        displayCheckmark: false,
        willRedirect: false,
        callback: () => {},
      };
    });
  }, []);

  useEffect(() => {
    validatePBLExpiration(payByLinkData, navigate, setError);
  }, [payByLinkData, navigate, setError]);

  const gotoUrl = (url: string) => {
    window.location.href = url;
  };
  const _paymentType = manifest?.paymentType || paymentType;
  const showEcheckForm = _paymentType === PaymentType.Echeck;
  const showEcheckAdyenForm = !isScheduled && manifest?.clientConfig?.renderAdyenEcheckForm === true;

  const showError = !(
    _paymentType &&
    [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid, PaymentType.Echeck].includes(_paymentType)
  );

  useEffect(() => {
    const validateManifest = async () => {
      try {
        await sources.getManifest(manifestId ?? '');
      } catch (e) {
        let errorMessage = 'Getting manifest failed';
        LogCannon.blast('Navigating to Error', {
          component: 'PaymentPage',
          errorMessage,
          error: typeof e === 'string' ? `${e}` : e,
          manifestId,
        });
        navigate(Pages.Error);
      }
    };
    validateManifest();
  }, []);

  if (showError) {
    const errorMessage = 'ERROR: Invalid or Unsupported Payment Type';
    LogCannon.blast('Navigating to Error', {
      component: 'PaymentPage',
      errorMessage,
      manifestId,
      paymentType: _paymentType,
    });
    navigate(Pages.Error);
  }

  useEffect(() => {
    const redirectUrl = redirectMap[manifestId!];
    if (redirectUrl) {
      gotoUrl(redirectUrl);
      return;
    }
  }, []);

  useEffect(() => {
    if (customerId && sessionId) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const isBackNavigation = navigationEntries.length > 0 && navigationEntries[0].type === 'back_forward';

      const flow = getPaymentPBLTaggingFlow(payByLinkData);
      const insurancePolicies = getPaymentPBLTaggingInsurancePolicies(payByLinkData);

      const smsPaymentTags = {
        channel: 'my account',
        subchannel1: 'sms',
        page: 'my account:sms:payment',
        sessionid: sessionId,
        custID: customerId,
        flow,
        ins_policy: insurancePolicies,
      };

      UtagHelper.tag(smsPaymentTags);
    }
  }, [customerId, sessionId]);

  useEffect(() => {
    if (
      _paymentType &&
      [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid].includes(_paymentType)
    ) {
      if (!isScheduled && manifest?.clientConfig?.renderAdyenCardForm) {
        setShowCardAdyen(true);
        setShowFDCardForm(false);
      } else {
        setShowCardAdyen(false);
        setShowFDCardForm(true);
      }
    }

    if (!manifest?.tags || tagged) {
      return;
    }
    const { tags, clientConfig } = manifest || {};
    const { channel, subchannel1, subchannel2 } = tags || {};

    const currentPage = 'payment';
    const updatedTags = tags?.page
      ? UtagHelper.tagPage(tags, currentPage)
      : {
          ...tags,
          page: [channel, subchannel1, subchannel2, currentPage].filter(Boolean).join(':'),
        };

    const allowedPaymentTypes = clientConfig?.allowedPaymentTypes || [];
    const payment_options = allowedPaymentTypes?.sort().join() || '';
    UtagHelper.tag({ ...updatedTags, sessionid: sessionId, payment_options });
    setTagged(true);
  }, [manifest, paymentType]);

  return (
    <>
      {isProcessing.status && (
        <Processing
          text={isProcessing.text}
          renderCindy={isProcessing.cindy}
          displayCheckmark={isProcessing.displayCheckmark}
          onFinished={onProcessingFinished}
        />
      )}
      {showFDCardForm && (
        <CardForm
          manifestId={manifestId!}
          amount={manifest?.amount!}
          paymentType={manifest?.paymentType || PaymentType.Undefined}
          autoPay={manifest?.autoPay}
          returnUrl={manifest?.redirect?.returnUrl!}
          tokenOnly={manifest?.tokenOnly}
          tags={manifest?.tags}
          appId={manifest?.appId}
          softErrorCode={manifest?.softErrorCode}
          scheduledDate={scheduledDate}
          isScheduled={isScheduled}
          paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
          setParentProcessing={setProcessing}
        />
      )}
      {showCardAdyen && (
        <CardFormAyden
          manifestId={manifestId!}
          amount={manifest?.amount!}
          paymentType={manifest?.paymentType || PaymentType.Undefined}
          autoPay={manifest?.autoPay}
          returnUrl={manifest?.redirect?.returnUrl!}
          tokenOnly={manifest?.tokenOnly}
          tags={manifest?.tags}
          appId={manifest?.appId}
          softErrorCode={manifest?.softErrorCode}
          scheduledDate={scheduledDate}
          isScheduled={isScheduled}
          paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
          setParentProcessing={setProcessing}
          isUnitTest={false}
        />
      )}
      {showEcheckForm &&
        (showEcheckAdyenForm ? (
          <ECheckFormAdyen
            manifestId={manifestId!}
            amount={manifest?.amount!}
            autoPay={manifest?.autoPay}
            returnUrl={manifest?.redirect.returnUrl!}
            appId={manifest?.appId}
            scheduledDate={scheduledDate}
            isScheduled={isScheduled}
            tokenOnly={manifest?.tokenOnly}
            paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
            setParentProcessing={setProcessing}
            hideWhileParentProcessing={isProcessing.status}
          />
        ) : (
          <ECheckForm
            manifestId={manifestId!}
            amount={manifest?.amount!}
            autoPay={manifest?.autoPay}
            returnUrl={manifest?.redirect.returnUrl!}
            appId={manifest?.appId}
            scheduledDate={scheduledDate}
            isScheduled={isScheduled}
            paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
            setParentProcessing={setProcessing}
          />
        ))}
      {showError && <></>}
    </>
  );
};

export default PaymentPage;
